import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { deriveTechProgress, enRouteEta, type TechProgressSource } from '@/utils/techProgress';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatDocDate } from '@/utils/locale';

interface TechProgressBadgeProps {
  job: TechProgressSource;
  className?: string;
}

/**
 * Compact "where is the technician" badge for lists: Not accepted / Declined /
 * Accepted / En route / On site / Finished. Renders nothing when there is
 * nothing to say (no tech, cancelled, pre-app completion).
 */
export function TechProgressBadge({ job, className }: TechProgressBadgeProps) {
  const { locale, timezone } = useCompanyLocale();
  const progress = deriveTechProgress(job);
  if (!progress) return null;

  const when = progress.at ? formatDocDate(progress.at, `${locale.shortDate} h:mm a`, timezone) : null;
  const eta = enRouteEta(job);
  const etaTime = eta ? formatDocDate(eta.expectedAt.toISOString(), 'h:mm a', timezone) : null;
  const detail = eta
    ? `ETA ${eta.minutes} min, sent ${formatDocDate(eta.sentAt, 'h:mm a', timezone)}. Expected about ${etaTime}${eta.late ? ', running late' : ''}.`
    : progress.declineReason
    ? `Reason: ${progress.declineReason}`
    : progress.holdReason
      ? `On hold: ${progress.holdReason}`
      : progress.awaitingReconfirm
        ? 'The job was sent to the technician again (reschedule or re-send); they have not confirmed yet.'
        : null;

  const badge = (
    <Badge variant={eta?.late ? 'warning' : progress.variant} className={className}>
      {progress.stage === 'rescheduled' ? 'Asked again' : progress.label}
      {eta && ` · ${eta.late ? 'late, ' : ''}ETA ${etaTime}`}
    </Badge>
  );

  if (!when && !detail) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{badge}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium">{progress.label}{when ? ` · ${when}` : ''}</p>
          {detail && <p className="text-muted-foreground">{detail}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
